import {
  formatHttpRequest,
  parseHttpRequest,
  resolveEffectiveHttpVersion,
} from '@terra/shared-utils';
import type { AiApplicationInterfaceTypeSchema } from '@terra/shared-contracts-ai-applications';
import type { z } from 'zod';

type AiApplicationInterfaceType = z.infer<
  typeof AiApplicationInterfaceTypeSchema
>;

const HTTP_REQUEST_INTERFACE_TYPES = new Set<AiApplicationInterfaceType>([
  'HTTP',
  'SSE',
]);

const PROTOCOL_REGEX = /^(https?:\/\/)/i;

const isValidUrl = (url: string): boolean => {
  const trimmed = url.trim();
  if (!trimmed || !PROTOCOL_REGEX.test(trimmed)) {
    return false;
  }

  try {
    new URL(trimmed);
    return true;
  } catch {
    return false;
  }
};

export function supportsInterfaceHttpRequest(
  type: AiApplicationInterfaceType,
): boolean {
  return HTTP_REQUEST_INTERFACE_TYPES.has(type);
}

const DEFAULT_PROBE_BODY = '{"message":"hello"}';

export function canScaffoldInterfaceHttpRequest(url: string): boolean {
  return isValidUrl(url);
}

export function buildDefaultHttpRequestFromUrl({
  url,
  body = DEFAULT_PROBE_BODY,
}: {
  url: string;
  body?: string;
}): string {
  if (!canScaffoldInterfaceHttpRequest(url)) {
    return '';
  }

  const parsed = new URL(url.trim());
  return formatHttpRequest({
    method: 'POST',
    url: parsed.toString(),
    headers: {
      Host: parsed.host,
      'Content-Type': 'application/json',
    },
    body,
    httpVersion: resolveEffectiveHttpVersion({}),
  });
}

function isJsonBodySnippet(value: string): boolean {
  const trimmed = value.trim();
  return trimmed.startsWith('{') || trimmed.startsWith('[');
}

export function coerceInterfaceHttpRequest({
  stored,
  url,
}: {
  stored: string | null | undefined;
  url: string;
}): string {
  if (!stored?.trim()) {
    if (!canScaffoldInterfaceHttpRequest(url)) {
      return '';
    }
    return buildDefaultHttpRequestFromUrl({ url });
  }

  if (isJsonBodySnippet(stored)) {
    if (!canScaffoldInterfaceHttpRequest(url)) {
      return stored.trim();
    }
    return buildDefaultHttpRequestFromUrl({ url, body: stored.trim() });
  }

  return stored;
}

export function resolveUrlFromInterfaceHttpRequest({
  rawRequest,
  fallbackUrl,
}: {
  rawRequest: string;
  fallbackUrl: string;
}): string {
  const parsed = parseHttpRequest(rawRequest.trim(), {
    originalUrl: fallbackUrl,
  });
  if (!parsed?.url) {
    return fallbackUrl;
  }

  if (parsed.url.startsWith('http://') || parsed.url.startsWith('https://')) {
    return parsed.url;
  }

  try {
    const fallback = new URL(fallbackUrl);
    return new URL(parsed.url, fallback.origin).toString();
  } catch {
    return fallbackUrl;
  }
}
